import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { Banner, ErrorNote, PageHeader, Spinner } from "../../components/ui";

type EditorConfigResponse = {
  documentServerUrl: string;
  config: Record<string, unknown> & { document: { title: string } };
  versionLabel: string;
};

type DocEditor = { destroyEditor: () => void };
declare global {
  interface Window {
    DocsAPI?: { DocEditor: new (id: string, config: unknown) => DocEditor };
  }
}

/** Load the document server's API script once per page. */
function loadDocsApi(base: string): Promise<void> {
  if (window.DocsAPI) return Promise.resolve();
  const src = `${base}/web-apps/apps/api/documents/api.js`;
  const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
  return new Promise((resolve, reject) => {
    const script = existing ?? document.createElement("script");
    script.addEventListener("load", () => resolve());
    script.addEventListener("error", () =>
      reject(new Error("The document editor could not be reached.")),
    );
    if (!existing) {
      script.src = src;
      document.body.appendChild(script);
    }
  });
}

/**
 * Edit a draft in ONLYOFFICE: Word, Excel, PowerPoint and PDF, text and images.
 *
 * Saving here never touches SharePoint. Each save becomes a new draft version
 * in the DMS; the file in SharePoint is updated when the draft is approved.
 */
export function EditorScreen() {
  const { documentId = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const editorRef = useRef<DocEditor | null>(null);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [saved, setSaved] = useState(false);

  const { data, error, isLoading } = useQuery({
    queryKey: ["editor-config", documentId],
    queryFn: () => api.get<EditorConfigResponse>(`/documents/${documentId}/editor-config`),
    // A config carries a signed, expiring link; never reuse a stale one.
    staleTime: 0,
    gcTime: 0,
  });

  useEffect(() => {
    if (!data) return;
    let cancelled = false;
    loadDocsApi(data.documentServerUrl)
      .then(() => {
        if (cancelled || !window.DocsAPI) return;
        editorRef.current = new window.DocsAPI.DocEditor("onlyoffice-editor", {
          ...data.config,
          width: "100%",
          height: "100%",
          events: {
            onDocumentStateChange: (event: { data: boolean }) => {
              // false means every change has reached the document server.
              if (!event.data) setSaved(true);
            },
          },
        });
      })
      .catch((caught: Error) => setLoadError(caught));
    return () => {
      cancelled = true;
      editorRef.current?.destroyEditor();
      editorRef.current = null;
    };
  }, [data]);

  const done = () => {
    editorRef.current?.destroyEditor();
    editorRef.current = null;
    void queryClient.invalidateQueries({ queryKey: ["document", documentId] });
    navigate(`/documents/${documentId}`);
  };

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  return (
    <div className="flex h-[calc(100vh-120px)] min-h-[560px] flex-col gap-3">
      <PageHeader
        eyebrow={`Editing from ${data.versionLabel}`}
        title={data.config.document.title}
        subtitle="Changes are saved in the DMS as a new draft version. SharePoint is updated when the draft is approved."
        actions={
          <>
            <Link to={`/documents/${documentId}`} className="btn-ghost no-underline">
              Cancel
            </Link>
            <button className="btn-accent" onClick={done}>
              Done editing
            </button>
          </>
        }
      />
      {saved && (
        <Banner tone="good">
          Saved. The new draft version appears on the document within a minute or
          two, once it has been indexed.
        </Banner>
      )}
      {loadError && <ErrorNote error={loadError} />}
      <div className="min-h-0 flex-1 overflow-hidden rounded-panel border border-line bg-surface">
        <div id="onlyoffice-editor" />
      </div>
    </div>
  );
}
